import React, { useCallback, useEffect, useState } from 'react';
import {
  exportReadinessSnapshot,
  getReadinessLatest,
  getReadinessRunStatus,
  markReadinessReady,
} from '../../services/orderXApi';
import { ReadinessCheckGroup } from './ReadinessCheckGroups';
import { PrimaryBlockerPanel } from './PrimaryBlockerPanel';
import { ReadinessRunProgress } from './ReadinessRunProgress';
import {
  formatReadinessTimestamp,
  groupChecksByOutcome,
  reportFromSnapshot,
  resolveOperatorUserId,
  scoreBadgeClass,
} from './readinessUtils';
import { useReadinessRun } from './useReadinessRun';

export function ReadinessDashboard() {
  const {
    running,
    runStartedAt,
    report,
    snapshotMeta,
    error,
    info,
    pollWarning,
    progress,
    setReport,
    setSnapshotMeta,
    setError,
    startRun,
    resumeRun,
    loadLatest,
  } = useReadinessRun();

  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);

  const refreshLatest = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await loadLatest();
    } catch (err) {
      setError(err?.message || 'Unable to load readiness snapshot.');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [loadLatest, setError]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const [snapshotResult, statusResult] = await Promise.allSettled([
        getReadinessLatest(),
        getReadinessRunStatus(),
      ]);

      if (cancelled) return;

      if (snapshotResult.status === 'fulfilled') {
        const snapshot = snapshotResult.value;
        if (!snapshot) {
          setReport(null);
          setSnapshotMeta(null);
        } else {
          setSnapshotMeta(snapshot);
          setReport(reportFromSnapshot(snapshot));
        }
      } else {
        setError(snapshotResult.reason?.message || 'Unable to load readiness snapshot.');
      }

      if (statusResult.status === 'fulfilled') {
        resumeRun(statusResult.value);
      }

      setLoading(false);
      setInitialLoadDone(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [resumeRun, setError, setReport, setSnapshotMeta]);

  const runFullAssessment = () =>
    startRun({
      run_mode: 'full',
      include_e2e: true,
      operator_user_id: resolveOperatorUserId() || undefined,
    });

  const handleExport = async () => {
    if (!snapshotMeta?.id) {
      setError('Run an assessment first to create a snapshot you can export.');
      return;
    }
    try {
      const payload = await exportReadinessSnapshot(snapshotMeta.id);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-genie-readiness-${snapshotMeta.id}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || 'Export failed.');
    }
  };

  const handleMarkReady = async () => {
    setMarkingReady(true);
    setError('');
    try {
      await markReadinessReady({ operator_user_id: resolveOperatorUserId() || undefined });
      await refreshLatest();
    } catch (err) {
      setError(err?.message || 'Unable to mark ready.');
    } finally {
      setMarkingReady(false);
    }
  };

  const groups = groupChecksByOutcome(report?.checks || []);
  const blockerFailCount = Number(report?.blocker_fail_count || 0);
  const snapshotTimestamp = snapshotMeta?.created_at || report?.completed_at;
  const formattedSnapshotTime = formatReadinessTimestamp(snapshotTimestamp);
  const formattedRunStartedAt = formatReadinessTimestamp(runStartedAt);

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <h5 className="mb-0">Readiness Dashboard</h5>
            {report ? (
              <span className={`badge ${scoreBadgeClass(report.readiness_score)}`}>
                {report.readiness_score}% ready
              </span>
            ) : null}
            {running ? <span className="badge text-bg-primary">Running</span> : null}
          </div>
          <div className="text-muted small mt-1">
            {running && formattedRunStartedAt
              ? `Assessment started: ${formattedRunStartedAt}`
              : formattedSnapshotTime
                ? `Last snapshot: ${formattedSnapshotTime}`
                : 'No readiness run recorded yet.'}
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-primary btn-sm" onClick={runFullAssessment} disabled={running}>
            {running ? 'Running in background…' : 'Run Full Assessment'}
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport} disabled={!snapshotMeta?.id || running}>
            Export JSON
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={handleMarkReady}
            disabled={markingReady || blockerFailCount > 0 || running}
          >
            {markingReady ? 'Marking…' : 'Mark Ready'}
          </button>
        </div>
      </div>

      {initialLoadDone && loading && !running ? (
        <div className="text-muted small mb-2">Refreshing readiness…</div>
      ) : null}
      {!initialLoadDone && loading ? <div className="text-muted">Loading latest readiness…</div> : null}
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {!error && pollWarning ? <div className="alert alert-warning py-2">{pollWarning}</div> : null}
      {running ? (
        <ReadinessRunProgress startedAt={runStartedAt} progress={progress} title="Running full assessment" />
      ) : info ? (
        <div className="alert alert-success py-2">{info}</div>
      ) : null}

      {!loading && !report && !running ? (
        <div className="alert alert-light border">
          No readiness snapshot found. Run a full assessment to generate your first report.
        </div>
      ) : null}

      {report ? <PrimaryBlockerPanel report={report} /> : null}

      <ReadinessCheckGroup title="Blockers" tone="danger" checks={groups.blockers} onHealSuccess={refreshLatest} />
      <ReadinessCheckGroup title="Warnings" tone="warning" checks={groups.warnings} onHealSuccess={refreshLatest} />
      <ReadinessCheckGroup title="Information" tone="info" checks={groups.information} defaultOpen />
      <ReadinessCheckGroup title="Passed" tone="success" checks={groups.passed} defaultOpen={false} />
      <ReadinessCheckGroup title="Skipped" tone="secondary" checks={groups.skipped} defaultOpen={false} />
    </div>
  );
}
